import { useDeviceType } from '@cian/ui-kit/responsive';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackPinClick } from './trackPinClick';
import { useHotspotClickHandler } from './useHotspotClickHandler';
import { IPinsLayer } from './usePinsLayer';
import { closeMobileListing } from '../../../../common/actions/listingModal';
import { openOfferPreview } from '../../../../common/actions/openOfferPreview/openOfferPreview';
import { TFeature } from '../../../../common/types/map';
import { IApplicationState, TThunkDispatch } from '../../../../common/types/redux';
import { useMapController } from '../../../controller/useMapController';
import { selectMapBounds } from '../../../selectors';
import { TMapBounds } from '../../../types/mapBounds';

interface IUsePinsClickHandlersParams extends IPinsLayer {
  isResultsAvailable: boolean;
  isDetailsVisible: boolean;
}

export function usePinsClickHandlers({
  hotspotsLayer,
  balloonManager,
  isResultsAvailable,
  isDetailsVisible,
}: IUsePinsClickHandlersParams): void {
  const dispatch = useDispatch<TThunkDispatch>();
  const deviceType = useDeviceType();
  const controller = useMapController();
  const { precision } = useSelector<IApplicationState, TMapBounds>(selectMapBounds);

  // Клик по "веснушкам" -> зум в область веснушки
  const handleHotspotClick = useHotspotClickHandler({ isResultsAvailable, isDetailsVisible });

  React.useEffect(() => {
    hotspotsLayer.layer.events.add('click', handleHotspotClick);

    return () => {
      hotspotsLayer.layer.events.remove('click', handleHotspotClick);
    };
  }, [hotspotsLayer, handleHotspotClick]);

  // Клик по пинам с ценами -> Открытие превью оффера на карте
  const handleBalloonClick = React.useCallback(
    (event: YMaps.IEvent) => {
      const objectId = event.get<string>('objectId');
      const feature = balloonManager.objectManager.objects.getById<TFeature>(objectId);

      if (!feature) {
        return;
      }

      const offerId = feature.properties.offers?.[0]?.cianId;
      const currentZoom = controller.getZoom();
      const zoom = Math.round(currentZoom);

      trackPinClick({ isDetailsVisible, offerId, zoom });

      // closeMobileListing - мобильная концепция: сбрасывает офферы общего листинга.
      // На десктопе листинг живёт и его трогать нельзя, иначе клик по пину чистит выдачу.
      if (deviceType !== 'desktop') {
        dispatch(closeMobileListing());
      }
      dispatch(
        openOfferPreview(feature, {
          precision,
          isDetailed: isDetailsVisible,
        }),
      );
    },
    [balloonManager, controller, dispatch, deviceType, precision, isDetailsVisible],
  );

  React.useEffect(() => {
    balloonManager.objectManager.objects.events.add('click', handleBalloonClick);

    return () => {
      balloonManager.objectManager.objects.events.remove('click', handleBalloonClick);
    };
  }, [balloonManager, handleBalloonClick]);
}
